"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { mediaUrl } from "@/lib/api";

interface Image {
  path: string;
}

interface Props {
  images: Image[];
  alt: string;
}

/**
 * Галерея товара с поддержкой:
 * - стрелок переключения (◀ ▶) на десктопе
 * - keyboard ←/→/Home/End (фокус ловит корневой div)
 * - горизонтального ряда миниатюр со scroll-snap
 * - swipe на мобиле
 * - лайтбокса по клику на главное фото (нативный <dialog>)
 * - счётчика "N/M" в правом нижнем углу
 * - preload соседних фото (N±1, N±2) для мгновенного переключения
 */
export function ProductGallery({ images, alt }: Props) {
  const [activeIdx, setActiveIdx] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const thumbsRef = useRef<HTMLDivElement>(null);
  const dialogRef = useRef<HTMLDialogElement>(null);

  const total = images.length;
  const clampIdx = useCallback(
    (i: number) => Math.max(0, Math.min(total - 1, i)),
    [total],
  );

  const go = useCallback(
    (delta: number) => setActiveIdx((i) => clampIdx(i + delta)),
    [clampIdx],
  );

  // Keyboard navigation на корневом div (tabIndex=0). Работает всегда, когда галерея в фокусе.
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowRight") {
      e.preventDefault();
      go(1);
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      go(-1);
    } else if (e.key === "Home") {
      e.preventDefault();
      setActiveIdx(0);
    } else if (e.key === "End") {
      e.preventDefault();
      setActiveIdx(total - 1);
    } else if (e.key === "Enter" || e.key === " ") {
      if ((e.target as HTMLElement).dataset.role === "main") {
        e.preventDefault();
        setLightboxOpen(true);
      }
    }
  };

  // Preload N±1, N±2 — мгновенное переключение
  useEffect(() => {
    [-2, -1, 1, 2].forEach((offset) => {
      const idx = activeIdx + offset;
      if (idx >= 0 && idx < total) {
        const img = new window.Image();
        const url = mediaUrl(images[idx].path);
        if (url) img.src = url;
      }
    });
  }, [activeIdx, images, total]);

  // Прокрутка активной миниатюры в видимую область горизонтального ряда.
  // Важно: используем ручной scrollLeft, а не scrollIntoView — последний скроллит
  // и страницу к thumbnail-контейнеру, вырывая закупщика от просмотра главного фото.
  useEffect(() => {
    const container = thumbsRef.current;
    const el = container?.querySelector<HTMLButtonElement>(`[data-thumb="${activeIdx}"]`);
    if (!container || !el) return;
    const elLeft = el.offsetLeft;
    const elRight = elLeft + el.offsetWidth;
    const viewLeft = container.scrollLeft;
    const viewRight = viewLeft + container.clientWidth;
    if (elLeft < viewLeft) {
      container.scrollTo({ left: elLeft, behavior: "smooth" });
    } else if (elRight > viewRight) {
      container.scrollTo({ left: elRight - container.clientWidth, behavior: "smooth" });
    }
  }, [activeIdx]);

  // Swipe handling для мобилы
  const touchStart = useRef<{ x: number; y: number } | null>(null);
  const onTouchStart = (e: React.TouchEvent) => {
    const t = e.touches[0];
    touchStart.current = { x: t.clientX, y: t.clientY };
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (!touchStart.current) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - touchStart.current.x;
    const dy = t.clientY - touchStart.current.y;
    // Direction lock: только если горизонтальное движение преобладает
    if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) {
      go(dx > 0 ? -1 : 1);
    }
    touchStart.current = null;
  };

  // Открытие / закрытие <dialog>
  useEffect(() => {
    const d = dialogRef.current;
    if (!d) return;
    if (lightboxOpen && !d.open) d.showModal();
    if (!lightboxOpen && d.open) d.close();
  }, [lightboxOpen]);

  if (total === 0) {
    return (
      // eslint-disable-next-line @next/next/no-img-element
      <img
        src="/placeholders/no-photo.jpg"
        alt={alt}
        className="aspect-[4/3] w-full rounded-[12px] bg-bg-muted object-contain"
      />
    );
  }

  const active = images[clampIdx(activeIdx)];
  const activeUrl = mediaUrl(active.path);

  return (
    <div
      ref={rootRef}
      tabIndex={0}
      role="region"
      aria-label={`Галерея товара: ${alt}`}
      onKeyDown={onKeyDown}
      className="rounded-[12px] focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500"
    >
      {/* Main image */}
      <div
        className="relative aspect-[4/3] overflow-hidden rounded-[12px] bg-bg-muted"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          data-role="main"
          src={activeUrl ?? "/placeholders/no-photo.jpg"}
          alt={alt}
          className="h-full w-full cursor-zoom-in object-contain"
          onClick={() => setLightboxOpen(true)}
          onError={(e) => {
            const t = e.currentTarget;
            if (!t.src.endsWith("/placeholders/no-photo.jpg")) {
              t.src = "/placeholders/no-photo.jpg";
            }
          }}
          onLoad={(e) => {
            const t = e.currentTarget;
            if (t.naturalWidth === 0 && !t.src.endsWith("/placeholders/no-photo.jpg")) {
              t.src = "/placeholders/no-photo.jpg";
            }
          }}
        />

        {/* Стрелки — только на десктопе, swipe на мобиле */}
        {total > 1 && (
          <>
            <button
              type="button"
              aria-label="Предыдущее фото"
              onClick={() => go(-1)}
              disabled={activeIdx === 0}
              className="absolute left-3 top-1/2 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/80 shadow-md backdrop-blur-sm transition-all hover:scale-105 hover:bg-white hover:shadow-lg disabled:opacity-30 disabled:hover:scale-100 md:flex"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" aria-hidden>
                <path d="M15 18l-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            <button
              type="button"
              aria-label="Следующее фото"
              onClick={() => go(1)}
              disabled={activeIdx === total - 1}
              className="absolute right-3 top-1/2 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/80 shadow-md backdrop-blur-sm transition-all hover:scale-105 hover:bg-white hover:shadow-lg disabled:opacity-30 disabled:hover:scale-100 md:flex"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" aria-hidden>
                <path d="M9 18l6-6-6-6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </>
        )}

        {/* Счётчик "N/M" — нужен на мобиле, полезен на десктопе */}
        {total > 1 && (
          <span
            aria-live="polite"
            className="tabular pointer-events-none absolute bottom-2 right-3 rounded bg-black/45 px-1.5 py-0.5 text-xs text-white"
          >
            {activeIdx + 1} / {total}
          </span>
        )}
      </div>

      {/* Миниатюры — одна горизонтальная строка с scroll-snap */}
      {total > 1 && (
        <div
          ref={thumbsRef}
          role="tablist"
          aria-label="Миниатюры фото товара"
          className="mt-3 flex gap-2 overflow-x-auto pb-1"
          style={{ scrollSnapType: "x mandatory", scrollbarWidth: "thin" }}
        >
          {images.map((img, idx) => {
            const url = mediaUrl(img.path);
            const isActive = idx === activeIdx;
            return (
              <button
                key={img.path}
                data-thumb={idx}
                role="tab"
                type="button"
                aria-selected={isActive}
                aria-label={`Фото ${idx + 1} из ${total}`}
                tabIndex={isActive ? 0 : -1}
                onClick={() => setActiveIdx(idx)}
                className={`shrink-0 rounded-[8px] border-2 bg-white p-0.5 transition-colors ${
                  isActive
                    ? "border-brand-500"
                    : "border-border hover:border-brand-500"
                }`}
                style={{
                  width: 72,
                  height: 72,
                  scrollSnapAlign: "start",
                }}
              >
                {url && (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img
                    src={url}
                    alt=""
                    loading="lazy"
                    onError={(e) => {
                      const t = e.currentTarget;
                      if (!t.src.endsWith("/placeholders/no-photo.jpg")) {
                        t.src = "/placeholders/no-photo.jpg";
                      }
                    }}
                    className="h-full w-full object-contain"
                  />
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Лайтбокс — нативный <dialog> */}
      <dialog
        ref={dialogRef}
        onClose={() => setLightboxOpen(false)}
        onClick={(e) => {
          // Закрытие по клику на backdrop (вне самого <img>)
          if ((e.target as HTMLElement).tagName === "DIALOG") setLightboxOpen(false);
        }}
        onKeyDown={(e) => {
          if (e.key === "ArrowRight") {
            e.preventDefault();
            go(1);
          } else if (e.key === "ArrowLeft") {
            e.preventDefault();
            go(-1);
          }
        }}
        className="m-0 h-screen max-h-none w-screen max-w-none border-0 bg-black/90 p-0 backdrop:bg-black/80"
      >
        <div className="relative flex h-full w-full items-center justify-center">
          {activeUrl && (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={activeUrl}
              alt={alt}
              className="max-h-[90vh] max-w-[90vw] object-contain"
            />
          )}
          <button
            type="button"
            aria-label="Закрыть"
            onClick={() => setLightboxOpen(false)}
            className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full bg-white/15 text-white backdrop-blur-sm hover:bg-white/25"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" aria-hidden>
              <path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" />
            </svg>
          </button>
          {total > 1 && (
            <>
              <button
                type="button"
                aria-label="Предыдущее фото"
                onClick={(e) => { e.stopPropagation(); go(-1); }}
                disabled={activeIdx === 0}
                className="absolute left-4 top-1/2 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/15 text-white backdrop-blur-sm hover:bg-white/25 disabled:opacity-30"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" aria-hidden>
                  <path d="M15 18l-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
              <button
                type="button"
                aria-label="Следующее фото"
                onClick={(e) => { e.stopPropagation(); go(1); }}
                disabled={activeIdx === total - 1}
                className="absolute right-4 top-1/2 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/15 text-white backdrop-blur-sm hover:bg-white/25 disabled:opacity-30"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" aria-hidden>
                  <path d="M9 18l6-6-6-6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
              <span className="tabular absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full bg-white/15 px-3 py-1 text-sm text-white backdrop-blur-sm">
                {activeIdx + 1} / {total}
              </span>
            </>
          )}
        </div>
      </dialog>
    </div>
  );
}
